import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  DatabaseBackup,
  Download,
  Upload,
  RefreshCw,
  HardDriveDownload,
  HardDriveUpload,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  FileArchive,
  Rocket,
  ExternalLink,
  Server,
  Tag,
  History,
  Clock,
  DatabaseZap,
  ListChecks,
} from "lucide-react";
import { toast } from "sonner";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return String(iso);
  }
};

const COLLECTION_LABELS = {
  users: "User Admin",
  services: "Layanan",
  doctors: "Dokter",
  news: "Berita",
  gallery: "Galeri",
  contact_messages: "Pesan Kontak",
  hero_slides: "Hero Slides",
  hero_stats: "Hero Stats",
  about_content: "Tentang Kami — Konten",
  about_pillars: "Tentang Kami — Pilar",
  laporan: "Laporan",
  site_content: "Konten Situs (Profil/Config)",
  audit_logs: "Audit Log",
};

export default function BackupPanel() {
  const { authAxios } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [version, setVersion] = useState("");

  const [exportUploads, setExportUploads] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const [importUploads, setImportUploads] = useState(true);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [syncingSchema, setSyncingSchema] = useState(false);
  const [schemaResult, setSchemaResult] = useState(null);

  const doEnsureSchema = async () => {
    setSyncingSchema(true);
    setSchemaResult(null);
    try {
      const { data } = await authAxios.post("/admin/db/ensure-schema");
      setSchemaResult(data);
      if (data.already_up_to_date) {
        toast.success("Struktur database sudah paling baru");
      } else {
        toast.success("Struktur database berhasil disinkronkan");
      }
      loadHistory();
    } catch (e) {
      const detail =
        e?.response?.data?.detail || "Gagal menyinkronkan struktur database";
      toast.error(detail);
    } finally {
      setSyncingSchema(false);
    }
  };

  const PROD_URL_KEY = "rsb_prod_admin_url";
  const [prodUrl, setProdUrl] = useState(
    () =>
      localStorage.getItem(PROD_URL_KEY) || "https://admin.rsbhayangkaratarakan.id"
  );

  const normalizedProdUrl = (prodUrl || "").trim().replace(/\/+$/, "");
  const prodBackupUrl = normalizedProdUrl
    ? `${normalizedProdUrl}/admin/backup`
    : "";

  const saveProdUrl = (v) => {
    setProdUrl(v);
    try {
      localStorage.setItem(PROD_URL_KEY, v);
    } catch (e) {
      /* ignore */
    }
  };

  const openProdAdmin = () => {
    if (!prodBackupUrl) {
      toast.error("Isi URL admin production dulu");
      return;
    }
    window.open(prodBackupUrl, "_blank", "noopener,noreferrer");
  };

  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const { data } = await authAxios.get("/admin/backup/summary");
      setSummary(data);
    } catch (e) {
      toast.error("Gagal memuat ringkasan data");
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await authAxios.get("/admin/backup/history?limit=20");
      setHistory(data.items || []);
    } catch (e) {
      // silent — history is non-critical
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadVersion = async () => {
    try {
      const { data } = await authAxios.get("/version");
      setVersion(data.version || "");
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    loadSummary();
    loadHistory();
    loadVersion();
  }, []);

  const doExport = async (includeUploadsArg) => {
    const inc =
      typeof includeUploadsArg === "boolean" ? includeUploadsArg : exportUploads;
    setExporting(true);
    try {
      const resp = await authAxios.get(
        `/admin/backup/export?include_uploads=${inc}`,
        { responseType: "blob" }
      );
      // Try to read filename from Content-Disposition
      let filename = `rsbt-data-backup-${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "")}.zip`;
      const cd = resp.headers?.["content-disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/);
        if (m) filename = m[1];
      }
      const blobUrl = URL.createObjectURL(
        new Blob([resp.data], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
      toast.success("Backup berhasil diunduh");
      loadHistory();
    } catch (e) {
      toast.error("Gagal membuat backup");
    } finally {
      setExporting(false);
    }
  };

  const doImport = async () => {
    if (!importFile) {
      toast.error("Pilih file backup (.zip) terlebih dahulu");
      return;
    }
    if (importMode === "replace" && !confirmReplace) {
      toast.error("Centang konfirmasi untuk mode Replace");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("mode", importMode);
      fd.append("include_uploads", importUploads ? "true" : "false");
      const { data } = await authAxios.post("/admin/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success("Restore selesai");
      loadSummary();
      loadHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Gagal mengimpor backup";
      toast.error(detail);
    } finally {
      setImporting(false);
    }
  };

  const counts = summary?.collections || {};
  const sortedKeys = Object.keys(counts).sort(
    (a, b) => (counts[b] || 0) - (counts[a] || 0)
  );

  return (
    <div className="max-w-5xl" data-testid="backup-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
          <DatabaseBackup className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-xl text-slate-900">
            Backup &amp; Restore Data
          </h1>
          <p className="text-sm text-slate-500">
            Pindahkan seluruh data (database + file upload) antar-environment:
            Production &rarr; Development &rarr; deploy ulang.
          </p>
        </div>
        </div>
        {version && (
          <span
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-medium"
            data-testid="app-version-badge"
            title="Versi aplikasi"
          >
            <Tag className="w-3.5 h-3.5" />
            v{version}
          </span>
        )}
      </div>

      {/* Info banner */}
      <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 text-sm text-indigo-900 flex gap-3">
        <FileArchive className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Alur yang disarankan</p>
          <ol className="list-decimal ml-4 mt-1 space-y-0.5 text-indigo-800">
            <li>Di <b>Production</b>: klik <b>Unduh Backup</b> untuk mengunduh 1 file ZIP.</li>
            <li>Di <b>Development</b>: unggah ZIP tadi lalu klik <b>Restore</b>.</li>
            <li>Lakukan revisi / pengembangan, lalu deploy ulang.</li>
          </ol>
          <p className="mt-1 text-indigo-700">
            Backup ini <b>DB-agnostic</b> — data dari MariaDB bisa di-restore ke MongoDB, dan sebaliknya.
          </p>
        </div>
      </div>

      {/* Sinkronkan Struktur Database */}
      <div
        className="mt-6 bg-white rounded-2xl border border-slate-200 p-5"
        data-testid="schema-sync-card"
      >
        <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <DatabaseZap className="w-4 h-4 text-indigo-600" />
          Sinkronkan Struktur Database
        </h2>
        <p className="text-sm text-slate-500 mb-3">
          Perbarui <b>struktur</b> database Production (tabel, kolom, index) agar
          sesuai versi terbaru — <b>tanpa menghapus atau mereset data</b>. Aman
          dijalankan kapan saja; hanya menambah yang belum ada.
        </p>

        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 text-xs text-emerald-800 flex gap-2 mb-4">
          <ListChecks className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Menjalankan <code>CREATE TABLE IF NOT EXISTS</code> +{" "}
            <code>ALTER TABLE</code> yang idempotent. Data yang sudah ada tidak
            akan tersentuh. Jalankan ini setelah deploy kode baru yang menambah
            kolom/tabel — tanpa perlu restart server.
          </div>
        </div>

        <button
          onClick={doEnsureSchema}
          disabled={syncingSchema}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm disabled:opacity-60"
          data-testid="schema-sync-btn"
        >
          {syncingSchema ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <DatabaseZap className="w-4 h-4" />
          )}
          {syncingSchema ? "Menyinkronkan..." : "Sinkronkan Struktur DB Sekarang"}
        </button>

        {schemaResult && (
          <div
            className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800"
            data-testid="schema-sync-result"
          >
            <div className="flex items-center gap-2 font-semibold mb-2 text-slate-900">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              {schemaResult.message ||
                (schemaResult.already_up_to_date
                  ? "Struktur sudah paling baru"
                  : "Struktur disinkronkan")}
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
                DB: <b>{schemaResult.db_type}</b>
              </span>
              <span className="px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
                Tabel dipastikan: <b>{(schemaResult.tables_ensured || []).length}</b>
              </span>
              {(schemaResult.tables_created || []).length > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                  Tabel baru: <b>{schemaResult.tables_created.length}</b>
                </span>
              )}
              {(schemaResult.columns_added || []).length > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                  Kolom baru: <b>{schemaResult.columns_added.length}</b>
                </span>
              )}
              {(schemaResult.indexes_added || []).length > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                  Index baru: <b>{schemaResult.indexes_added.length}</b>
                </span>
              )}
            </div>
            {(schemaResult.columns_added?.length > 0 ||
              schemaResult.indexes_added?.length > 0 ||
              schemaResult.tables_created?.length > 0) && (
              <ul className="mt-2 ml-4 list-disc text-xs text-slate-600 space-y-0.5">
                {(schemaResult.tables_created || []).map((t) => (
                  <li key={`t-${t}`}>Tabel dibuat: <code>{t}</code></li>
                ))}
                {(schemaResult.columns_added || []).map((c) => (
                  <li key={`c-${c}`}>Kolom ditambah: <code>{c}</code></li>
                ))}
                {(schemaResult.indexes_added || []).map((i) => (
                  <li key={`i-${i}`}>Index ditambah: <code>{i}</code></li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Deploy & Sync to Production — guided workflow */}
      <div
        className="mt-6 bg-white rounded-2xl border border-slate-200 p-5"
        data-testid="deploy-sync-guide"
      >
        <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-1">
          <Rocket className="w-4 h-4 text-indigo-600" />
          Deploy &amp; Sinkronisasi ke Production
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Alur terpandu untuk mengirim perubahan data dari Development ke Production
          (mode <b>Merge</b>, <b>database saja</b>).
        </p>

        {/* Steps */}
        <ol className="space-y-3">
          {/* Step 1 */}
          <li className="flex gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
              1
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">
                Unduh backup database dari Development
              </p>
              <p className="text-xs text-slate-500 mb-2">
                File ZIP berisi seluruh data (tanpa file upload). Simpan di komputer Anda.
              </p>
              <button
                onClick={() => doExport(false)}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-60"
                data-testid="deploy-export-db-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Unduh Backup Database (DB saja)
              </button>
            </div>
          </li>

          {/* Step 2 */}
          <li className="flex gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
              2
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">
                Deploy kode terbaru ke Production
              </p>
              <p className="text-xs text-slate-500">
                Gunakan tombol <b>Deploy</b> / <b>Save to GitHub</b> di platform hosting Anda
                untuk menerbitkan perubahan kode. (Langkah ini di luar aplikasi.) Cukup sekali
                setiap ada perubahan kode — untuk perubahan data saja, lompat ke langkah 3.
              </p>
            </div>
          </li>

          {/* Step 3 */}
          <li className="flex gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
              3
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800">
                Buka Admin Production &amp; Import (mode Merge)
              </p>
              <p className="text-xs text-slate-500 mb-2">
                Login sebagai superadmin di Production, buka menu Backup &amp; Restore,
                unggah ZIP tadi, pilih mode <b>Merge</b>, lalu Restore.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="flex items-center gap-2 flex-1">
                  <Server className="w-4 h-4 text-slate-400 shrink-0" />
                  <input
                    type="text"
                    value={prodUrl}
                    onChange={(e) => saveProdUrl(e.target.value)}
                    placeholder="https://admin.rsbhayangkaratarakan.id"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono"
                    data-testid="deploy-prod-url"
                  />
                </div>
                <button
                  onClick={openProdAdmin}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium shrink-0"
                  data-testid="deploy-open-prod-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  Buka Admin Production
                </button>
              </div>
              {prodBackupUrl && (
                <p className="text-[11px] text-slate-400 mt-1 font-mono break-all">
                  {prodBackupUrl}
                </p>
              )}
            </div>
          </li>
        </ol>

        {/* Tip */}
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <b>Tips agar Merge memperbarui data (bukan menduplikasi):</b> mulai pengembangan
            dari data Production. Restore dulu backup Production ke Development (bagian
            Import di bawah) sehingga ID data sama — perubahan Anda akan menimpa data yang
            benar saat di-Merge ke Production.
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <HardDriveDownload className="w-4 h-4 text-slate-500" />
            Ringkasan Data Saat Ini
          </h2>
          <button
            onClick={loadSummary}
            className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-sm"
            data-testid="backup-refresh-summary"
          >
            <RefreshCw className={`w-4 h-4 ${loadingSummary ? "animate-spin" : ""}`} />
            Segarkan
          </button>
        </div>

        {loadingSummary ? (
          <div className="py-8 text-center text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Menghitung...
          </div>
        ) : summary ? (
          <>
            <div className="flex flex-wrap gap-3 mb-4 text-sm">
              <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700">
                DB: <b>{summary.db_type}</b>
              </span>
              <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700">
                Total record: <b>{summary.total_records}</b>
              </span>
              <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-700">
                File upload: <b>{summary.uploads?.files ?? 0}</b> ({fmtBytes(summary.uploads?.bytes)})
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="backup-summary-grid">
              {sortedKeys.map((k) => (
                <div
                  key={k}
                  className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-100 bg-slate-50/60"
                >
                  <span className="text-xs text-slate-600 truncate mr-2">
                    {COLLECTION_LABELS[k] || k}
                  </span>
                  <span className="text-sm font-semibold text-slate-900">{counts[k]}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-400">Tidak ada data.</p>
        )}
      </div>

      {/* Export */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5">
        <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-3">
          <Download className="w-4 h-4 text-slate-500" />
          Export (Unduh Backup)
        </h2>
        <label className="flex items-center gap-2 text-sm text-slate-700 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={exportUploads}
            onChange={(e) => setExportUploads(e.target.checked)}
            className="w-4 h-4 accent-indigo-600"
            data-testid="backup-export-uploads"
          />
          Sertakan file upload (gambar, PDF)
        </label>
        <button
          onClick={doExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm disabled:opacity-60"
          data-testid="backup-export-btn"
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {exporting ? "Menyiapkan..." : "Unduh Backup (.zip)"}
        </button>
      </div>

      {/* Import */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-5">
        <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-3">
          <HardDriveUpload className="w-4 h-4 text-slate-500" />
          Import (Restore Backup)
        </h2>

        <input
          type="file"
          accept=".zip,application/zip"
          onChange={(e) => {
            setImportFile(e.target.files?.[0] || null);
            setImportResult(null);
          }}
          className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 mb-4"
          data-testid="backup-import-file"
        />

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Mode Restore</label>
            <select
              value={importMode}
              onChange={(e) => {
                setImportMode(e.target.value);
                setConfirmReplace(false);
              }}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              data-testid="backup-import-mode"
            >
              <option value="merge">Merge — tambah/timpa berdasarkan ID (aman)</option>
              <option value="replace">Replace — kosongkan lalu isi ulang</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={importUploads}
                onChange={(e) => setImportUploads(e.target.checked)}
                className="w-4 h-4 accent-indigo-600"
                data-testid="backup-import-uploads"
              />
              Pulihkan file upload dari ZIP
            </label>
          </div>
        </div>

        {importMode === "replace" && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Peringatan: mode Replace</p>
                <p className="text-red-700">
                  Setiap koleksi yang ada di file backup akan <b>dikosongkan</b> lalu diisi ulang.
                  Data lokal yang tidak ada di backup akan hilang.
                </p>
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmReplace}
                    onChange={(e) => setConfirmReplace(e.target.checked)}
                    className="w-4 h-4 accent-red-600"
                    data-testid="backup-confirm-replace"
                  />
                  Saya paham dan ingin melanjutkan
                </label>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={doImport}
          disabled={importing || !importFile}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm disabled:opacity-60"
          data-testid="backup-import-btn"
        >
          {importing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {importing ? "Mengimpor..." : "Restore Sekarang"}
        </button>

        {importResult && (
          <div
            className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
            data-testid="backup-import-result"
          >
            <div className="flex items-center gap-2 font-semibold mb-2">
              <CheckCircle2 className="w-4 h-4" />
              Restore selesai (mode: {importResult.mode})
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {Object.entries(importResult.imported || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between px-2 py-1 rounded bg-white/60">
                  <span className="text-emerald-800 truncate mr-2">
                    {COLLECTION_LABELS[k] || k}
                  </span>
                  <span className="font-semibold">{v}</span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-emerald-700">
              File upload dipulihkan: <b>{importResult.restored_files}</b>
            </p>
          </div>
        )}
      </div>

      {/* Riwayat Sinkronisasi */}
      <div
        className="mt-6 bg-white rounded-2xl border border-slate-200 p-5"
        data-testid="backup-history"
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-500" />
            Riwayat Sinkronisasi
          </h2>
          <button
            onClick={loadHistory}
            className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-sm"
            data-testid="backup-history-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loadingHistory ? "animate-spin" : ""}`} />
            Segarkan
          </button>
        </div>

        {loadingHistory ? (
          <div className="py-6 text-center text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Memuat riwayat...
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">
            Belum ada aktivitas backup atau restore.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="backup-history-list">
            {history.map((h) => {
              const isExport = h.action === "backup.export";
              return (
                <li key={h.id} className="py-3 flex items-start gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      isExport
                        ? "bg-emerald-50 text-emerald-600"
                        : "bg-indigo-50 text-indigo-600"
                    }`}
                  >
                    {isExport ? (
                      <Download className="w-4 h-4" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">
                        {isExport ? "Backup dibuat" : "Data dipulihkan"}
                      </span>
                      {!isExport && h.mode && (
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full ${
                            h.mode === "replace"
                              ? "bg-red-50 text-red-600"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {h.mode}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {fmtDateTime(h.created_at)} &middot; oleh {h.actor_email}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {h.total_records} record
                      {isExport
                        ? h.include_uploads
                          ? " · dengan file upload"
                          : " · DB saja"
                        : typeof h.restored_files === "number"
                        ? ` · ${h.restored_files} file dipulihkan`
                        : ""}
                    </div>
                    {h.filename && (
                      <div className="text-[11px] text-slate-400 font-mono truncate mt-0.5">
                        {h.filename}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
